import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { useClientsQuery } from '@/features/Clients/Data';
import type { TipoSolicitacao } from '@/features/SI/models';
import { useMeusEquipamentosQuery, useSolicitacoesPorAlocacaoResponsavelQuery } from '@/features/SI/hooks/data';
import SolicitacaoDrawer from '@/features/SI/pages/SolicitacoesListPage/components/SolicitacaoDrawer';
import NovaSolicitacaoDrawer from '@/features/SI/pages/SolicitacoesListPage/components/NovaSolicitacaoDrawer';
import {
  FILTRO_SOLICITACOES_PADRAO,
  SI_SOLICITACOES_LISTAS,
  filtrarSolicitacoes,
  getVisibleSiSolicitacaoCards,
} from './siSolicitacoesPage.definitions';
import type { FiltroSolicitacoes, SiSolicitacoesLista as ListaId } from './siSolicitacoesPage.definitions';
import { useMinhasSolicitacoes } from './useMinhasSolicitacoes';
import { SiSolicitacaoCardButton } from './components/SiSolicitacaoCardButton';
import { SiSolicitacoesLista } from './components/SiSolicitacoesLista';
import { SiSolicitacoesFiltros } from './components/SiSolicitacoesFiltros';
import { MeusEquipamentos } from './components/MeusEquipamentos';
import { InformarDivergenciaDrawer } from './components/InformarDivergenciaDrawer';

/**
 * Aba "Solicitações" do Portal do SI — cartões por tipo pra abrir (como "Solicitações ao DP")
 * e, abaixo, abas lado a lado: "Minhas solicitações" (antiga "Que eu abri"; pro si_rh, inclui
 * as do time RH), "Da minha equipe" (antiga "Minha Equipe", só pra quem tem alguma) e "Meus
 * equipamentos". A aba vem de `?lista=` (deep-link e tour). Tudo abre como visão de
 * solicitante — os controles de execução do SI ficam só na aba "Controle de Solicitações".
 */
export default function SiSolicitacoesPage() {
  const { user, userHasRole } = useAuth();
  const cards = useMemo(() => getVisibleSiSolicitacaoCards(userHasRole), [userHasRole]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [nova, setNova] = useState<{ tipo: TipoSolicitacao; equipamentoIds?: string[] } | null>(null);
  const [divergenciaAberta, setDivergenciaAberta] = useState(false);
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroSolicitacoes>(FILTRO_SOLICITACOES_PADRAO);

  const { data: clientes = [] } = useClientsQuery();
  const minhas = useMinhasSolicitacoes();
  const equipe = useSolicitacoesPorAlocacaoResponsavelQuery(user?.collaboratorId);
  const meusEquipamentos = useMeusEquipamentosQuery();
  const temEquipe = (equipe.data?.length ?? 0) > 0;

  const listas = SI_SOLICITACOES_LISTAS.filter((lista) => lista.id !== 'equipe' || temEquipe);
  const listaParam = searchParams.get('lista') as ListaId | null;
  const listaAtiva: ListaId = listas.some((lista) => lista.id === listaParam) ? (listaParam as ListaId) : 'minhas';
  const trocarLista = (id: ListaId) => setSearchParams(id === 'minhas' ? {} : { lista: id }, { replace: true });

  const nomeCliente = useCallback(
    (clienteId: string | null | undefined) => clientes.find((cliente) => cliente.id === clienteId)?.name,
    [clientes]
  );
  const consulta = listaAtiva === 'equipe' ? { data: equipe.data, isLoading: equipe.isLoading } : minhas;
  const itens = useMemo(
    () => filtrarSolicitacoes(consulta.data ?? [], filtro, nomeCliente),
    [consulta.data, filtro, nomeCliente]
  );
  const filtroAtivo = filtro.texto.trim() !== '' || filtro.tipo !== '' || filtro.status !== 'em_aberto';
  const totalEquipamentos = meusEquipamentos.data?.length ?? 0;

  const abrirCriada = (id: string) => {
    trocarLista('minhas');
    setSelecionadaId(id);
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-tour="si-sol-cartoes">
        {cards.map((card) => (
          <SiSolicitacaoCardButton key={card.tipo} card={card} onClick={() => setNova({ tipo: card.tipo })} />
        ))}
      </div>

      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div role="tablist" aria-label="Listas" className="flex flex-wrap gap-1" data-tour="si-sol-listas">
            {listas.map((lista) => (
              <button
                key={lista.id}
                type="button"
                role="tab"
                data-tour={lista.id === 'equipamentos' ? 'si-sol-equipamentos' : undefined}
                aria-selected={listaAtiva === lista.id}
                onClick={() => trocarLista(lista.id)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-montserrat text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
                  listaAtiva === lista.id
                    ? 'bg-primary-50 font-semibold text-primary-700 ring-1 ring-primary-200'
                    : 'font-medium text-raro-gray-600 hover:bg-white hover:text-primary-700'
                )}
              >
                {lista.label}
                {lista.id === 'equipamentos' && totalEquipamentos > 0 && (
                  <span className="rounded-full bg-primary-600 px-1.5 text-xs font-semibold text-white">{totalEquipamentos}</span>
                )}
              </button>
            ))}
          </div>
          {listaAtiva !== 'equipamentos' && (
            <div data-tour="si-sol-filtros">
              <SiSolicitacoesFiltros filtro={filtro} onChange={setFiltro} />
            </div>
          )}
        </div>

        {listaAtiva === 'equipe' && (
          <p className="text-xs text-raro-gray-500">Solicitações das alocações em que você é o responsável.</p>
        )}
        {listaAtiva === 'minhas' && minhas.ehRh && (
          <p className="text-xs text-raro-gray-500">Inclui as abertas pelo DP — você também pode editar.</p>
        )}

        {listaAtiva === 'equipamentos' ? (
          <MeusEquipamentos
            equipamentos={meusEquipamentos.data ?? []}
            isLoading={meusEquipamentos.isLoading}
            isError={meusEquipamentos.isError}
            onDevolver={(equipamento) => setNova({ tipo: 'devolucao', equipamentoIds: [equipamento.id] })}
            onPedirTroca={(equipamento) => setNova({ tipo: 'troca_equipamento', equipamentoIds: [equipamento.id] })}
            onAcompanhar={setSelecionadaId}
            onInformarDivergencia={() => setDivergenciaAberta(true)}
          />
        ) : (
          <div data-tour="si-sol-lista">
          <SiSolicitacoesLista
            itens={itens}
            isLoading={consulta.isLoading}
            nomeCliente={nomeCliente}
            abertaPor={listaAtiva === 'minhas' ? minhas.abertaPor : undefined}
            vazio={
              filtroAtivo
                ? 'Nenhuma solicitação com esse filtro.'
                : listaAtiva === 'equipe'
                  ? 'Nenhuma solicitação da sua equipe em aberto.'
                  : 'Você não tem solicitações em aberto.'
            }
            onSelect={setSelecionadaId}
          />
          </div>
        )}
      </section>

      <SolicitacaoDrawer solicitacaoId={selecionadaId} forcarVisaoSolicitante onClose={() => setSelecionadaId(null)} />
      <NovaSolicitacaoDrawer
        isOpen={nova !== null}
        onClose={() => setNova(null)}
        valoresIniciais={nova ? { tipoSolicitacao: nova.tipo, equipamentoIds: nova.equipamentoIds } : undefined}
        onCriada={(id) => {
          setNova(null);
          abrirCriada(id);
        }}
      />
      <InformarDivergenciaDrawer
        isOpen={divergenciaAberta}
        equipamentos={meusEquipamentos.data ?? []}
        onClose={() => setDivergenciaAberta(false)}
        onCriada={(id) => {
          setDivergenciaAberta(false);
          abrirCriada(id);
        }}
      />
    </div>
  );
}
